import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Box, Button, LinearProgress, Chip, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import WebcamCard, { WebcamCardHandle, VideoQualityWarning } from '../components/WebcamCard';
import SignalsPanel from '../components/SignalsPanel';
import HealthCard from '../components/HealthCard';
import { useStatsSettings } from '../context/Context';
import { useNavigate } from 'react-router-dom';
import { hrFromBvp, appendBvp } from '../utils/hrFromBvp';
import { getModelCapabilities } from '../utils/modelCapabilities';

// ── Template signal generators ────────────────────────────────────────────────
const BASE = { hr: 72, sbp: 118, dbp: 75, spo2: 98, hrv: 55, rr: 14 };
const jitter = (v: number, r: number) => Math.round(v + (Math.random() - 0.5) * r);

const generatePPG = (hr = 72, n = 180, fs = 30) => {
  const out: number[] = [];
  const basePeriod = (fs * 60) / hr;
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const period = basePeriod * (1 + (Math.random() - 0.5) * 0.015);
    phase = (phase + 1) % period;
    const p = phase / period;
    // Systolic peak (FWHM≈150ms) + dicrotic notch (FWHM≈120ms) + respiratory drift
    out.push(
      1.00 * Math.exp(-((p - 0.15) ** 2) / 0.012) +
      0.25 * Math.exp(-((p - 0.43) ** 2) / 0.008) +
      0.05 * Math.sin(2 * Math.PI * (BASE.rr / 60) * (i / fs)) +
      (Math.random() - 0.5) * 0.03,
    );
  }
  return out;
};

const generateECG = (hr = 72, n = 180, fs = 30) => {
  const out: number[] = [];
  const basePeriod = (fs * 60) / hr;
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const period = basePeriod * (1 + (Math.random() - 0.5) * 0.015);
    phase = (phase + 1) % period;
    const p = phase / period;
    // Widths chosen so each feature spans ≥2 frames at 30fps / 72bpm
    out.push(
      0.15  * Math.exp(-((p - 0.08) ** 2) / 0.0040) +  // P wave
      -0.12 * Math.exp(-((p - 0.18) ** 2) / 0.0018) +  // Q wave
       1.00 * Math.exp(-((p - 0.21) ** 2) / 0.0030) +  // R wave
      -0.30 * Math.exp(-((p - 0.25) ** 2) / 0.0018) +  // S wave
       0.28 * Math.exp(-((p - 0.55) ** 2) / 0.0090) +  // T wave
      (Math.random() - 0.5) * 0.02,
    );
  }
  return out;
};

const EXPECTED_FRAMES = 180;

const DemoVideoPage: React.FC = () => {
  const navigate = useNavigate();
  const { healthData, updateHealthData, healthHistory, selectedStats, selectedModel } = useStatsSettings();
  const modelCapabilities = useMemo(() => getModelCapabilities(selectedModel), [selectedModel]);
  const webcamCardRef = useRef<WebcamCardHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateAppliedRef = useRef(false);
  const bvpAccRef = useRef<number[]>([]);
  const BVP_NEW_PER_WINDOW = 10;

  const [videoUrl, setVideoUrl] = useState('');
  const [videoName, setVideoName] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstResponseReceived, setIsFirstResponseReceived] = useState(false);
  const [mediapipeFailed, setMediapipeFailed] = useState(false);
  const [inferenceError, setInferenceError] = useState('');
  const [inferenceProgress, setInferenceProgress] = useState(0);
  const [framesCollected, setFramesCollected] = useState(0);
  const [framesRequired, setFramesRequired] = useState(0);
  const [qualityWarnings, setQualityWarnings] = useState<VideoQualityWarning[]>([]);
  const [ppgSignal, setPpgSignal] = useState<number[]>([]);
  const [ecgSignal, setEcgSignal] = useState<number[]>([]);
  const [icgSignal, setIcgSignal] = useState<number[]>([]);
  const [bpSignal, setBpSignal] = useState<number[]>([]);
  const [bufferFrames, setBufferFrames] = useState<number | undefined>(undefined);
  const [hasBP, setHasBP] = useState(false);
  const [scanSeconds, setScanSeconds] = useState(0);

  useEffect(() => () => { if (videoUrl) URL.revokeObjectURL(videoUrl); }, [videoUrl]);

  const loadVideo = useCallback((file: File) => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(URL.createObjectURL(file));
    setVideoName(file.name);
    setIsMonitoring(false);
    setIsFirstResponseReceived(false);
    setInferenceProgress(0);
    setPpgSignal([]);
    setEcgSignal([]);
    setIcgSignal([]);
    setBpSignal([]);
  }, [videoUrl]);

  const handleApiResponse = useCallback((result: {
    HR: number; HRV: number; RR: number;
    SBP: number[] | null; DBP: number[] | null;
    PPG: number[]; ECG: number[]; ICG?: number[]; BP_wave?: number[]; MAP?: number | null; SpO2?: number | null; buffer_frames?: number;
    signal_quality?: { snr_db: number; warnings: string[] };
  }) => {
    const newSamples = result.PPG.slice(-BVP_NEW_PER_WINDOW);
    bvpAccRef.current = appendBvp(bvpAccRef.current, newSamples, 1800);

    if (!templateAppliedRef.current) {
      templateAppliedRef.current = true;
      setIsFirstResponseReceived(true);
      setInferenceProgress(100);
      const sbp = result.SBP?.[0] ?? jitter(BASE.sbp, 6);
      const dbp = result.DBP?.[0] ?? jitter(BASE.dbp, 4);
      const initHR = Math.round(Math.max(40, Math.min(180, result.HR)));
      const initHRV = Math.round(Math.max(20, Math.min(100, result.HRV)));
      const initRR  = Math.round(Math.max(8,  Math.min(30,  result.RR)));
      setHasBP(true);
      updateHealthData({ heartRate: initHR, systolicBP: Math.round(sbp), diastolicBP: Math.round(dbp), ...(result.MAP != null ? { map: Math.round(result.MAP) } : {}), spo2: result.SpO2 != null ? Math.round(result.SpO2) : jitter(BASE.spo2, 2), hrv: initHRV, respiratoryRate: initRR });
      setPpgSignal(result.PPG.length > 0 ? result.PPG : generatePPG(initHR));
      setEcgSignal(generateECG(initHR));
      if (result.ICG && result.ICG.length > 0) setIcgSignal(result.ICG);
      if (result.BP_wave && result.BP_wave.length > 0) setBpSignal(result.BP_wave);
      if (typeof result.buffer_frames === 'number') setBufferFrames(result.buffer_frames);
    }

    if (bvpAccRef.current.length >= 300) {
      const computedHR = hrFromBvp(bvpAccRef.current, 30);
      if (isFinite(computedHR)) {
        updateHealthData({ heartRate: Math.round(computedHR) });
      }
    }

    if (result.PPG.length > 0) setPpgSignal(result.PPG);
  }, [updateHealthData]);

  const handleApiError = useCallback((message: string) => {
    // Previously silently ignored — left the UI stuck on "Collecting…"
    // forever with no visible sign anything was wrong whenever the GPU
    // box was actually down (504s), console errors aside.
    setInferenceError(message);
    setIsMonitoring(false);
    setIsLoading(false);
    setInferenceProgress(0);
    setFramesCollected(0);
    setFramesRequired(0);
  }, []);

  const handleMediaPipeError = useCallback((failed: boolean) => {
    setMediapipeFailed(failed);
    if (failed) { setIsMonitoring(false); setIsLoading(false); }
  }, []);

  const handleQualityWarning = useCallback((warnings: VideoQualityWarning[]) => {
    setQualityWarnings(warnings);
  }, []);

  const handleInferenceProgress = useCallback((progress: number, collected: number, required: number) => {
    setInferenceProgress(progress);
    setFramesCollected(collected);
    setFramesRequired(required);
  }, []);

  const handleStart = () => {
    if (!videoUrl) return;
    templateAppliedRef.current = false;
    bvpAccRef.current = [];
    setIsLoading(true);
    setIsFirstResponseReceived(false);
    setInferenceError('');
    setInferenceProgress(0);
    setFramesCollected(0);
    setFramesRequired(EXPECTED_FRAMES);
    setScanSeconds(0);
    setTimeout(() => {
      setIsMonitoring(true);
      setIsLoading(false);
      webcamCardRef.current?.toggleInference();
    }, 800);
  };

  const handleStop = () => {
    templateAppliedRef.current = false;
    bvpAccRef.current = [];
    setIsMonitoring(false);
    setInferenceProgress(0);
    setFramesCollected(0);
    setFramesRequired(0);
    setScanSeconds(0);
    setHasBP(false);
    webcamCardRef.current?.toggleInference();
  };

  useEffect(() => {
    if (!isMonitoring || !isFirstResponseReceived) return;
    const t = setInterval(() => setScanSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [isMonitoring, isFirstResponseReceived]);

  const hasData = isMonitoring && isFirstResponseReceived;

  const computeWellness = () => {
    if (!hasData) return { score: 0, label: '—', color: '#60a5fa' };
    const hr = healthData.heartRate;
    const hrScore = hr >= 60 && hr <= 80 ? 100 : hr >= 50 && hr <= 100 ? 80 : 50;
    const spo2Score = healthData.spo2 >= 98 ? 100 : healthData.spo2 >= 95 ? 75 : 40;
    const sbp = healthData.systolicBP < 120 ? 100 : healthData.systolicBP < 130 ? 75 : healthData.systolicBP < 140 ? 50 : 20;
    const dbp = healthData.diastolicBP < 80 ? 100 : healthData.diastolicBP < 90 ? 60 : 25;
    const rrScore = healthData.respiratoryRate >= 12 && healthData.respiratoryRate <= 20 ? 100 : 60;
    const hrvScore = Math.min(100, (healthData.hrv / 50) * 100);
    const score = Math.round(Math.max(0, Math.min(100, hrScore * 0.30 + spo2Score * 0.25 + ((sbp + dbp) / 2) * 0.25 + rrScore * 0.10 + hrvScore * 0.10)));
    const label = score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 50 ? 'Fair' : 'Low';
    const color = score >= 85 ? '#4ade80' : score >= 70 ? '#60a5fa' : score >= 50 ? '#fbbf24' : '#f87171';
    return { score, label, color };
  };
  const wellness = computeWellness();

  const total = framesRequired || EXPECTED_FRAMES;
  const secsLeft = Math.max(0, Math.ceil((total - framesCollected) / 30));

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)', px: { xs: 1.5, md: 3, lg: 4 }, py: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, maxWidth: 1600, mx: 'auto', width: '100%' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&:hover': { color: 'white', bgcolor: 'transparent' } }}>
          Back
        </Button>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '4px', textTransform: 'uppercase' }}>
          SeeVita · Demo
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 80, justifyContent: 'flex-end' }}>
          {isMonitoring && !mediapipeFailed && (
            <>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: hasData ? '#4ade80' : '#fbbf24', boxShadow: hasData ? '0 0 6px #4ade80' : '0 0 6px #fbbf24', animation: 'pulse 1.8s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: hasData ? '#4ade80' : '#fbbf24' }}>
                {hasData ? 'Live' : 'Analyzing…'}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {inferenceError && (
        <Alert severity="warning" sx={{ borderRadius: '12px', py: 0.5 }}>
          {inferenceError}
        </Alert>
      )}

      {/* Main grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr', lg: '420px 1fr' }, gap: { xs: 1.5, md: 2.5 }, flex: 1, minHeight: 0, alignItems: 'start' }}>

        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {videoUrl ? (
            <Box sx={{ ...cardSx, p: 0, overflow: 'hidden', position: 'relative' }}>
              <Box sx={{ width: '100%', aspectRatio: { xs: '3 / 4', md: '4 / 3' }, overflow: 'hidden', borderRadius: '20px' }}>
                <WebcamCard
                  ref={webcamCardRef}
                  source="video"
                  videoSrc={videoUrl}
                  mode="api"
                  hideControls
                  onApiResponse={handleApiResponse}
                  onApiError={handleApiError}
                  onMediaPipeError={handleMediaPipeError}
                  onQualityWarning={handleQualityWarning}
                  onInferenceProgress={handleInferenceProgress}
                />
              </Box>
              <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.55)', borderRadius: '10px', px: 1.2, py: 0.6 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                  {videoName}
                </Typography>
                <Button size="small" onClick={() => { handleStop(); setVideoUrl(''); setVideoName(''); }}
                  sx={{ color: '#60a5fa', textTransform: 'none', fontSize: '0.65rem', minWidth: 0, p: '2px 6px' }}>
                  Change
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{ ...cardSx, aspectRatio: '4/3', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 1, border: isDragging ? '2px dashed #60a5fa' : '2px dashed rgba(255,255,255,0.12)', transition: 'border-color 0.2s' }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f?.type.startsWith('video/')) loadVideo(f); }}
            >
              <UploadFileIcon sx={{ fontSize: 36, color: isDragging ? '#60a5fa' : 'rgba(255,255,255,0.25)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', textAlign: 'center' }}>Drop a video file here</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '0.7rem' }}>or click to browse</Typography>
            </Box>
          )}

          <input ref={fileInputRef} type="file" accept="video/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) loadVideo(f); }} />

          {/* Quality warnings */}
          {isMonitoring && qualityWarnings.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {qualityWarnings.map(w => (
                <Chip key={w} label={QUALITY_WARNING_LABELS[w] ?? w} size="small"
                  sx={{ bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', fontSize: '0.68rem', fontWeight: 600, height: 22 }} />
              ))}
            </Box>
          )}

          {/* Countdown */}
          {isMonitoring && !isFirstResponseReceived && !mediapipeFailed && (
            <Box sx={{ ...cardSx, py: 1.5, gap: 1, alignItems: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>Analyzing</Typography>
              <Typography sx={{ fontSize: '3rem', fontWeight: 900, lineHeight: 1, color: secsLeft <= 2 ? '#4ade80' : '#60a5fa', fontVariantNumeric: 'tabular-nums', transition: 'color 0.4s ease' }}>
                {secsLeft}
              </Typography>
              <LinearProgress variant="determinate" value={inferenceProgress}
                sx={{ width: '100%', height: 4, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.07)', '& .MuiLinearProgress-bar': { bgcolor: secsLeft <= 2 ? '#4ade80' : '#60a5fa', borderRadius: 999, transition: 'background-color 0.4s ease' } }} />
            </Box>
          )}

          {/* Start / Stop */}
          <Button variant="contained" onClick={isMonitoring ? handleStop : handleStart} disabled={isLoading || !videoUrl}
            sx={{
              py: 1.4, borderRadius: '50px', fontSize: '0.88rem', fontWeight: 700, textTransform: 'none',
              bgcolor: isMonitoring ? 'transparent' : 'white', color: isMonitoring ? '#f87171' : '#0a1628',
              border: isMonitoring ? '1.5px solid rgba(248,113,113,0.45)' : 'none',
              boxShadow: isMonitoring ? 'none' : '0 4px 24px rgba(255,255,255,0.12)', transition: 'all 0.2s ease',
              '&:hover': { bgcolor: isMonitoring ? 'rgba(239,68,68,0.08)' : '#e8f0fe', transform: 'translateY(-1px)' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.18)', boxShadow: 'none', border: 'none' },
            }}>
            {isLoading ? 'Initializing…' : isMonitoring ? 'Stop Demo' : videoUrl ? 'Start Demo' : 'Upload a video first'}
          </Button>

          {/* Scan duration */}
          {isMonitoring && isFirstResponseReceived && (() => {
            const RECOMMENDED = 30;
            const pct = Math.min(100, Math.round((scanSeconds / RECOMMENDED) * 100));
            const done = scanSeconds >= RECOMMENDED;
            return (
              <Box sx={{ ...cardSx, py: 1.5, gap: 1, alignItems: 'center' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                    {done ? 'Great scan ✓' : 'Scan in progress'}
                  </Typography>
                  <Typography sx={{ color: done ? '#4ade80' : 'rgba(255,255,255,0.3)', fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums' }}>
                    {scanSeconds}s {!done && `/ ${RECOMMENDED}s`}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ width: '100%', height: 4, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.07)', '& .MuiLinearProgress-bar': { bgcolor: done ? '#4ade80' : '#60a5fa', borderRadius: 999, transition: 'background-color 0.4s ease' } }} />
              </Box>
            );
          })()}
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, minHeight: 0 }}>

          <Box sx={cardSx}>
            <Typography sx={cardLabelSx}>Health Stats</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.2, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', opacity: hasData ? 1 : 0.3, transition: 'opacity 0.5s ease', mb: 0.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase' }}>Wellness Score</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', mt: 0.3 }}>
                  {hasData ? wellness.label : 'Start a scan to see your score'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '2.4rem', fontWeight: 900, color: wellness.color, lineHeight: 1 }}>
                {hasData ? wellness.score : '—'}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(100px, 1fr))', md: 'repeat(auto-fit, minmax(120px, 1fr))' }, gridAutoRows: '1fr', gap: { xs: 1, md: 1.5 }, opacity: hasData ? 1 : 0.3, transition: 'opacity 0.5s ease' }}>
              {selectedStats.heartRate && modelCapabilities.vitals.heartRate && <HealthCard title="Heart Rate" value={hasData ? healthData.heartRate : '—'} unit="BPM" bgColor="rgba(239,68,68,0.15)" textColor="#f87171" history={healthHistory.heartRate} />}
              {(selectedStats.systolicBP || selectedStats.diastolicBP) && modelCapabilities.vitals.systolicBP && <HealthCard title="Blood Pressure" value={hasData && hasBP ? `${healthData.systolicBP}/${healthData.diastolicBP}` : '—'} unit={hasData && hasBP && selectedStats.map && modelCapabilities.vitals.map ? `mmHg · MAP ${healthData.map}` : 'mmHg'} bgColor="rgba(59,130,246,0.15)" textColor="#60a5fa" history={healthHistory.systolicBP} />}
              {selectedStats.respiratoryRate && modelCapabilities.vitals.respiratoryRate && <HealthCard title="Resp. Rate" value={hasData ? healthData.respiratoryRate : '—'} unit="/min" bgColor="rgba(20,184,166,0.15)" textColor="#2dd4bf" history={healthHistory.respiratoryRate} />}
              {selectedStats.spo2 && modelCapabilities.vitals.spo2 && <HealthCard title="SpO2" value={hasData ? healthData.spo2 : '—'} unit="%" bgColor="rgba(34,197,94,0.15)" textColor="#4ade80" history={healthHistory.spo2} />}
              {selectedStats.hrv && modelCapabilities.vitals.hrv && <HealthCard title="HRV" value={hasData ? healthData.hrv : '—'} unit="ms" bgColor="rgba(99,102,241,0.15)" textColor="#818cf8" history={healthHistory.hrv} />}
            </Box>
          </Box>

          <Box sx={cardSx}>
            <Typography sx={cardLabelSx}>Signals</Typography>
            <SignalsPanel isMonitoring={isMonitoring && !mediapipeFailed} ppgSignal={ppgSignal} ecgSignal={ecgSignal} icgSignal={icgSignal} bpSignal={bpSignal} bufferFrames={bufferFrames} showEcg={modelCapabilities.waveforms.ecg} showIcg={modelCapabilities.waveforms.icg} showBp={modelCapabilities.waveforms.bp} />
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

const cardSx = { background: 'rgba(10, 28, 60, 0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)', p: { xs: 1.5, md: 2 }, display: 'flex', flexDirection: 'column' as const };
const cardLabelSx = { fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '2.5px', textTransform: 'uppercase' as const, mb: { xs: 1, md: 1.5 } };

const QUALITY_WARNING_LABELS: Record<string, string> = {
  face_too_small: '⚠ Move closer',
  face_clipped: '⚠ Face partially out of frame',
  excessive_movement: '⚠ Hold still',
  low_resolution: '⚠ Low camera resolution',
  low_brightness: '⚠ Too dark',
  noisy_signal: '⚠ Noisy signal',
  hr_out_of_range: '⚠ HR reading unreliable',
};

export default DemoVideoPage;
